import { useAuth0, type User } from "@auth0/auth0-react"
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react"
import { useSessionStorage } from "usehooks-ts"
import { api } from "~/utils/api"
import { isProduction } from "~/utils/environment"
import { SIMULATED_ROLE_STORAGE_KEY } from "~/utils/roleSimulation"
import {
  type Permissions,
  ROLE,
  hasStaffRole,
  permissionsForRoles,
  rolesFromClaim,
} from "~/server/api/utils/roles"

export enum UserRole {
  READ_ONLY = "read_only",
  USER = "user",
  ADMIN = "admin",
  HAKIO = "hakio", // allows access to internal APi's
}

/** The role a JWT claim resolves to, highest tier wins — the `UserRole` mirror of
 * `permissionsForRoles`. A claim with no recognized tier role is `USER`. */
function roleFromClaim(roles: string[]): UserRole {
  if (hasStaffRole(roles)) return UserRole.HAKIO
  if (roles.includes(ROLE.ADMIN)) return UserRole.ADMIN
  if (roles.includes(ROLE.READ_ONLY)) return UserRole.READ_ONLY
  return UserRole.USER
}

function fePermissionsForRole(role: UserRole): Permissions {
  // `UserRole` values are exactly the claim strings the contract recognizes, so
  // derive permissions straight from the role — keeping the FE matrix in sync
  // with `~/server/api/utils/roles` (`HAKIO` → full staff access).
  return permissionsForRoles([role])
}

export const userRoleToRoleId = {
  [UserRole.READ_ONLY]: "rol_Y9HuU56XkLPcySpY",
  [UserRole.USER]: "", // No role for user
  [UserRole.ADMIN]: "rol_eqP86bGL95u814zH",
}

interface UserContextType {
  role: UserRole
  permissions: Permissions
  setRole: (newRole: UserRole) => void
  /** True when the real JWT identity carries a Hakio staff role. Gates
   * staff-only UI and the (non-prod) role simulator. */
  isStaff: boolean
  /** True until Auth0 resolves the session. Until then the roles claim is empty, so
   * `permissions` holds the default `user` tier rather than the real user's. */
  isAuthLoading: boolean
  /** The viewer's Auth0 `sub`. `undefined` whenever Auth0 has no resolved user: during
   * startup, and during a silent token renewal, which does not unmount the app. */
  userId: string | undefined
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoading: isAuthLoading } = useAuth0()

  const roles = rolesFromClaim(user)
  // Staff-ness comes from the role claim (the `hakio`/`staff` role), not the
  // email domain — the single source of truth shared with the backend.
  const isStaff = hasStaffRole(roles)

  // Also read per-request by the tRPC client (simulatedRoleHeader), which sends
  // it as x-hakio-simulated-role so the server enforces the simulated role too.
  // Cached query data is NOT invalidated on switch: responses are org-scoped and
  // roles only gate access, except the role-scoped adjustment reads refetched below.
  const [overrideRole, setOverrideRole] = useSessionStorage<UserRole | null>(
    SIMULATED_ROLE_STORAGE_KEY,
    null,
  )

  // Non-production only, matching `readSimulatedRole`: the client's permissions and the
  // `x-hakio-simulated-role` header must resolve to the same role.
  const simulating = isStaff && !isProduction()

  // Derived, never stored: a role switch or a refreshed claim takes effect immediately.
  const role = overrideRole && simulating ? overrideRole : roleFromClaim(roles)

  const permissions = useMemo(() => fePermissionsForRole(role), [role])

  const utils = api.useUtils()
  const overridePermissionRole = useCallback(
    (newRole: UserRole) => {
      if (simulating) {
        setOverrideRole(newRole)
        // Non-staff don't see the new-channel system adjustment (visibleAdjustmentsSql).
        void utils.adjustment.invalidate()
        void utils.variantTableData.variants.invalidate()
        void utils.forecasts.getNpiPipelineInfo.invalidate()
      } else {
        console.warn("Role override is not permitted for this user.")
      }
    },
    [simulating, setOverrideRole, utils],
  )

  // Every permission-gated primitive subscribes here: a new value identity re-renders all of
  // them, memoized or not.
  const value = useMemo(
    () => ({
      role,
      permissions,
      setRole: overridePermissionRole,
      isStaff,
      isAuthLoading,
      userId: user?.sub,
    }),
    [role, permissions, overridePermissionRole, isStaff, isAuthLoading, user?.sub],
  )

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function isDashboardUser(user: User | undefined) {
  return user?.email === "admin@hakio.com"
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) throw new Error("useUser must be used within a UserProvider")
  return context
}

/** Like `useUser`, but returns `undefined` outside a `UserProvider` instead of throwing: every
 * gated primitive calls `usePermission`, and a missing provider must not crash the surface. */
export const useOptionalUser = () => useContext(UserContext)
